import { useState } from 'react';
import { Card } from './Card';
import { Button } from './Button';
import { Textarea } from './Textarea';
import { Select } from './Select';
import { Input } from './Input';
import { computeHash } from '../services/hashService';
import { HashAlgorithm, EncodingFormat, HashResult } from '../types';
import { translations } from '../i18n/hu';
import { Copy, Check } from 'lucide-react';

const algorithms: { value: HashAlgorithm; label: string }[] = [
  { value: 'MD5', label: 'MD5' },
  { value: 'SHA-1', label: 'SHA-1' },
  { value: 'SHA-224', label: 'SHA-224' },
  { value: 'SHA-256', label: 'SHA-256' },
  { value: 'SHA-384', label: 'SHA-384' },
  { value: 'SHA-512', label: 'SHA-512' },
  { value: 'SHA-512/224', label: 'SHA-512/224' },
  { value: 'SHA-512/256', label: 'SHA-512/256' },
  { value: 'SHA3-224', label: 'SHA3-224' },
  { value: 'SHA3-256', label: 'SHA3-256' },
  { value: 'SHA3-384', label: 'SHA3-384' },
  { value: 'SHA3-512', label: 'SHA3-512' },
  { value: 'BLAKE2b', label: 'BLAKE2b' },
  { value: 'BLAKE2s', label: 'BLAKE2s' },
  { value: 'BLAKE3', label: 'BLAKE3' },
  { value: 'RIPEMD-160', label: 'RIPEMD-160' },
  { value: 'CRC32', label: 'CRC32' },
  { value: 'Adler-32', label: 'Adler-32' },
  { value: 'MurmurHash3', label: 'MurmurHash3' },
  { value: 'xxHash32', label: 'xxHash32' },
  { value: 'xxHash64', label: 'xxHash64' },
  { value: 'HMAC-MD5', label: 'HMAC-MD5' },
  { value: 'HMAC-SHA1', label: 'HMAC-SHA1' },
  { value: 'HMAC-SHA256', label: 'HMAC-SHA256' },
  { value: 'HMAC-SHA512', label: 'HMAC-SHA512' },
];

const encodings: { value: EncodingFormat; label: string }[] = [
  { value: 'hex', label: translations.encoding.hex },
  { value: 'hex-upper', label: translations.encoding.hexUpper },
  { value: 'base64', label: translations.encoding.base64 },
  { value: 'base32', label: translations.encoding.base32 },
  { value: 'base58', label: translations.encoding.base58 },
  { value: 'base62', label: translations.encoding.base62 },
  { value: 'base85', label: translations.encoding.base85 },
  { value: 'binary', label: translations.encoding.binary },
];

export function TextHash() {
  const [text, setText] = useState('');
  const [algorithm, setAlgorithm] = useState<HashAlgorithm>('SHA-256');
  const [encoding, setEncoding] = useState<EncodingFormat>('hex');
  const [key, setKey] = useState('');
  const [result, setResult] = useState<HashResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string>('');

  const handleGenerate = async () => {
    if (!text.trim()) {
      setError('Kérjük, adjon meg szöveget');
      return;
    }

    if (algorithm.startsWith('HMAC-') && !key.trim()) {
      setError('HMAC-hez kulcs szükséges');
      return;
    }

    setIsLoading(true);
    setError('');
    setCopied(false);

    try {
      const hashResult = await computeHash(
        text,
        algorithm,
        encoding,
        key ? { key } : undefined
      );
      setResult(hashResult);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ismeretlen hiba történt');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = () => {
    if (result) {
      navigator.clipboard.writeText(result.hash);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const needsKey = algorithm.startsWith('HMAC-');

  return (
    <div className="space-y-6">
      <Card title="Szöveg Hash Generálás">
        <div className="space-y-4">
          <Textarea
            label="Szöveg"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={translations.input.textPlaceholder}
            rows={6}
            className="font-mono text-sm"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label={translations.algorithm.label}
              value={algorithm}
              onChange={(e) => setAlgorithm(e.target.value as HashAlgorithm)}
              options={algorithms}
            />
            <Select
              label={translations.encoding.label}
              value={encoding}
              onChange={(e) => setEncoding(e.target.value as EncodingFormat)}
              options={encodings}
            />
          </div>

          {needsKey && (
            <Input
              label={translations.options.key}
              type="text"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="HMAC kulcs"
            />
          )}

          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          <Button
            onClick={handleGenerate}
            isLoading={isLoading}
            disabled={!text.trim() || (needsKey && !key.trim())}
            className="w-full"
          >
            {translations.common.generate}
          </Button>
        </div>
      </Card>

      {result && (
        <Card title={translations.result.title}>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                {translations.result.hash}
              </label>
              <div className="flex items-center gap-2">
                <div className="flex-1 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                  <p className="font-mono text-sm break-all text-gray-900 dark:text-white">
                    {result.hash}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopy}
                  className="flex-shrink-0"
                >
                  {copied ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600 dark:text-gray-400">
                  {translations.result.algorithm}:
                </span>
                <span className="ml-2 font-medium text-gray-900 dark:text-white">
                  {result.algorithm}
                </span>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">
                  {translations.result.encoding}:
                </span>
                <span className="ml-2 font-medium text-gray-900 dark:text-white">
                  {result.encoding}
                </span>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">
                  {translations.result.duration}:
                </span>
                <span className="ml-2 font-medium text-gray-900 dark:text-white">
                  {result.durationMs.toFixed(2)} ms
                </span>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">
                  {translations.result.inputLength}:
                </span>
                <span className="ml-2 font-medium text-gray-900 dark:text-white">
                  {result.inputLength} bájt
                </span>
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

